import useAuth from '@app/auth/use-auth';
import { EventProperties } from '@segment/analytics-next';
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { AnalyticsContext } from './AnalyticsContext';
import { analyticsConfig } from './config';
import segment from './segment';

export const AnalyticsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (analyticsConfig.loadAnalytics) {
      segment.load({
        writeKey: analyticsConfig.segmentWriteKey,
      });
      setEnabled(true);
    }
  }, []);

  useEffect(() => {
    if (!user || !user.uuid || !enabled) return;
    if (user.isStaff) {
      setEnabled(false);
    } else {
      segment.identify(user.uuid, {
        [analyticsConfig.groupKey]: [user.organizationUuid],
      });
    }
  }, [enabled, user]);

  const resetAnalytics = useCallback(() => {
    if (analyticsConfig.loadAnalytics) {
      setEnabled(true);
      segment.reset();
    }
  }, []);

  const track = useCallback(
    (event: string, properties?: EventProperties) => {
      if (!enabled || !user) return;
      const eventProperties = {
        ...properties,
        [analyticsConfig.groupKey]: [user?.organizationUuid],
      };
      segment.track(event, eventProperties);
    },
    [enabled, user]
  );

  const trackPage = useCallback(
    (page?: string) => {
      if (!enabled || !user) return;
      segment?.page(undefined, page, {
        [analyticsConfig.groupKey]: [user.organizationUuid],
      });
    },
    [enabled, user]
  );

  const value = {
    resetAnalytics,
    track,
    trackPage,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
